// contexts/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState('light');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [sidebarPinned, setSidebarPinned] = useState(true);
    const [compactMode, setCompactMode] = useState(false);
    const [colorScheme, setColorScheme] = useState('blue');
    const [fontSize, setFontSize] = useState('medium');
    const [highContrast, setHighContrast] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    const [preferences, setPreferences] = useState({
        language: 'en',
        timezone: 'Asia/Kolkata',
        dateFormat: 'DD/MM/YYYY',
        timeFormat: '24h',
        currency: 'INR',
        notifications: {
            email: true,
            push: true,
            sms: false,
            desktop: true,
            sound: true
        },
        dashboard: {
            showWelcomeMessage: true,
            defaultView: 'grid',
            autoRefresh: true,
            refreshInterval: 30000, // 30 seconds
            showQuickStats: true,
            compactCards: false
        },
        tables: {
            defaultPageSize: 20,
            showDensity: 'comfortable', // comfortable, compact, spacious
            stickyHeader: true,
            zebraStripes: false
        },
        maps: {
            defaultZoom: 10,
            showTraffic: true,
            showSatellite: false,
            clusterMarkers: true,
            animateTransitions: true
        }
    });

    // Load saved preferences from localStorage
    useEffect(() => {
        try {
            const savedTheme = localStorage.getItem('trackTrace_theme');
            const savedSidebarState = localStorage.getItem('trackTrace_sidebar_collapsed');
            const savedSidebarPinned = localStorage.getItem('trackTrace_sidebar_pinned');
            const savedPreferences = localStorage.getItem('trackTrace_preferences');
            const savedColorScheme = localStorage.getItem('trackTrace_color_scheme');
            const savedFontSize = localStorage.getItem('trackTrace_font_size');
            const savedCompactMode = localStorage.getItem('trackTrace_compact_mode');
            const savedHighContrast = localStorage.getItem('trackTrace_high_contrast');
            const savedReducedMotion = localStorage.getItem('trackTrace_reduced_motion');

            if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
                setTheme(savedTheme);
            }

            if (savedSidebarState !== null) {
                setSidebarCollapsed(JSON.parse(savedSidebarState));
            }

            if (savedSidebarPinned !== null) {
                setSidebarPinned(JSON.parse(savedSidebarPinned));
            }

            if (savedPreferences) {
                const parsed = JSON.parse(savedPreferences);
                setPreferences(prev => ({ ...prev, ...parsed }));
            }

            if (savedColorScheme) {
                setColorScheme(savedColorScheme);
            }

            if (savedFontSize) {
                setFontSize(savedFontSize);
            }

            if (savedCompactMode !== null) {
                setCompactMode(JSON.parse(savedCompactMode));
            }

            if (savedHighContrast !== null) {
                setHighContrast(JSON.parse(savedHighContrast));
            }

            if (savedReducedMotion !== null) {
                setReducedMotion(JSON.parse(savedReducedMotion));
            }
        } catch (error) {
            console.error('Error loading theme preferences:', error);
        }
    }, []);

    // Auto-detect theme based on system preference
    useEffect(() => {
        if (theme === 'auto') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = (e) => {
                applyTheme(e.matches ? 'dark' : 'light');
            };

            handleChange(mediaQuery);
            mediaQuery.addEventListener('change', handleChange);

            return () => mediaQuery.removeEventListener('change', handleChange);
        } else {
            applyTheme(theme);
        }
    }, [theme]);

    // Auto-detect reduced motion preference
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const handleChange = (e) => {
            setReducedMotion(e.matches);
        };

        handleChange(mediaQuery);
        mediaQuery.addEventListener('change', handleChange);

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Apply theme to document
    const applyTheme = useCallback((currentTheme) => {
        const root = document.documentElement;

        if (currentTheme === 'dark') {
            root.classList.add('dark');
            root.classList.remove('light');
        } else {
            root.classList.add('light');
            root.classList.remove('dark');
        }

        // Apply color scheme
        root.setAttribute('data-color-scheme', colorScheme);

        // Apply font size
        root.setAttribute('data-font-size', fontSize);

        // Apply compact mode
        if (compactMode) {
            root.classList.add('compact-mode');
        } else {
            root.classList.remove('compact-mode');
        }

        // Apply high contrast
        if (highContrast) {
            root.classList.add('high-contrast');
        } else {
            root.classList.remove('high-contrast');
        }

        // Apply reduced motion
        if (reducedMotion) {
            root.classList.add('reduced-motion');
        } else {
            root.classList.remove('reduced-motion');
        }

        // Update meta theme color
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            const colors = {
                light: '#ffffff',
                dark: '#1f2937',
                blue: '#2563eb',
                green: '#059669',
                purple: '#7c3aed'
            };
            metaThemeColor.setAttribute('content', colors[currentTheme] || colors[colorScheme] || colors.light);
        }
    }, [colorScheme, fontSize, compactMode, highContrast, reducedMotion]);

    // Save preferences to localStorage
    useEffect(() => {
        localStorage.setItem('trackTrace_theme', theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem('trackTrace_sidebar_collapsed', JSON.stringify(sidebarCollapsed));
    }, [sidebarCollapsed]);

    useEffect(() => {
        localStorage.setItem('trackTrace_sidebar_pinned', JSON.stringify(sidebarPinned));
    }, [sidebarPinned]);

    useEffect(() => {
        localStorage.setItem('trackTrace_preferences', JSON.stringify(preferences));
    }, [preferences]);

    useEffect(() => {
        localStorage.setItem('trackTrace_color_scheme', colorScheme);
    }, [colorScheme]);

    useEffect(() => {
        localStorage.setItem('trackTrace_font_size', fontSize);
    }, [fontSize]);

    useEffect(() => {
        localStorage.setItem('trackTrace_compact_mode', JSON.stringify(compactMode));
    }, [compactMode]);

    useEffect(() => {
        localStorage.setItem('trackTrace_high_contrast', JSON.stringify(highContrast));
    }, [highContrast]);

    useEffect(() => {
        localStorage.setItem('trackTrace_reduced_motion', JSON.stringify(reducedMotion));
    }, [reducedMotion]);

    // Theme actions
    const toggleTheme = useCallback(() => {
        setTheme(prev => {
            if (prev === 'light') return 'dark';
            if (prev === 'dark') return 'auto';
            return 'light';
        });
    }, []);

    const setThemeMode = useCallback((mode) => {
        if (['light', 'dark', 'auto'].includes(mode)) {
            setTheme(mode);
        }
    }, []);

    // Sidebar actions
    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(prev => !prev);
    }, []);

    const toggleSidebarPin = useCallback(() => {
        setSidebarPinned(prev => !prev);
    }, []);

    // Preferences actions
    const updatePreferences = useCallback((newPreferences) => {
        setPreferences(prev => {
            if (typeof newPreferences === 'function') {
                return { ...prev, ...newPreferences(prev) };
            }
            return { ...prev, ...newPreferences };
        });
    }, []);

    const updateNotificationPreferences = useCallback((notificationPrefs) => {
        setPreferences(prev => ({
            ...prev,
            notifications: { ...prev.notifications, ...notificationPrefs }
        }));
    }, []);

    const updateDashboardPreferences = useCallback((dashboardPrefs) => {
        setPreferences(prev => ({
            ...prev,
            dashboard: { ...prev.dashboard, ...dashboardPrefs }
        }));
    }, []);

    const updateTablePreferences = useCallback((tablePrefs) => {
        setPreferences(prev => ({
            ...prev,
            tables: { ...prev.tables, ...tablePrefs }
        }));
    }, []);

    const updateMapPreferences = useCallback((mapPrefs) => {
        setPreferences(prev => ({
            ...prev,
            maps: { ...prev.maps, ...mapPrefs }
        }));
    }, []);

    // Accessibility actions
    const toggleCompactMode = useCallback(() => {
        setCompactMode(prev => !prev);
    }, []);

    const toggleHighContrast = useCallback(() => {
        setHighContrast(prev => !prev);
    }, []);

    const toggleReducedMotion = useCallback(() => {
        setReducedMotion(prev => !prev);
    }, []);

    // Color scheme actions
    const setColorSchemeMode = useCallback((scheme) => {
        const validSchemes = ['blue', 'green', 'purple', 'red', 'orange', 'teal'];
        if (validSchemes.includes(scheme)) {
            setColorScheme(scheme);
        }
    }, []);

    // Font size actions
    const setFontSizeMode = useCallback((size) => {
        const validSizes = ['small', 'medium', 'large', 'extra-large'];
        if (validSizes.includes(size)) {
            setFontSize(size);
        }
    }, []);

    // Reset all preferences
    const resetPreferences = useCallback(() => {
        setTheme('light');
        setSidebarCollapsed(false);
        setSidebarPinned(true);
        setCompactMode(false);
        setColorScheme('blue');
        setFontSize('medium');
        setHighContrast(false);
        setReducedMotion(false);
        setPreferences({
            language: 'en',
            timezone: 'Asia/Kolkata',
            dateFormat: 'DD/MM/YYYY',
            timeFormat: '24h',
            currency: 'INR',
            notifications: {
                email: true,
                push: true,
                sms: false,
                desktop: true,
                sound: true
            },
            dashboard: {
                showWelcomeMessage: true,
                defaultView: 'grid',
                autoRefresh: true,
                refreshInterval: 30000,
                showQuickStats: true,
                compactCards: false
            },
            tables: {
                defaultPageSize: 20,
                showDensity: 'comfortable',
                stickyHeader: true,
                zebraStripes: false
            },
            maps: {
                defaultZoom: 10,
                showTraffic: true,
                showSatellite: false,
                clusterMarkers: true,
                animateTransitions: true
            }
        });
    }, []);

    // Get current effective theme (resolved from 'auto')
    const getEffectiveTheme = useCallback(() => {
        if (theme === 'auto') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return theme;
    }, [theme]);

    // Check if dark mode is active
    const isDarkMode = getEffectiveTheme() === 'dark';

    // Export/Import preferences
    const exportPreferences = useCallback(() => {
        const exportData = {
            theme,
            sidebarCollapsed,
            sidebarPinned,
            compactMode,
            colorScheme,
            fontSize,
            highContrast,
            reducedMotion,
            preferences,
            exportDate: new Date().toISOString(),
            version: '1.0.0'
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `trackTrace-preferences-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
    }, [theme, sidebarCollapsed, sidebarPinned, compactMode, colorScheme, fontSize, highContrast, reducedMotion, preferences]);

    const importPreferences = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const importData = JSON.parse(e.target.result);

                    // Validate import data
                    if (importData.version && importData.exportDate) {
                        if (importData.theme) setTheme(importData.theme);
                        if (importData.sidebarCollapsed !== undefined) setSidebarCollapsed(importData.sidebarCollapsed);
                        if (importData.sidebarPinned !== undefined) setSidebarPinned(importData.sidebarPinned);
                        if (importData.compactMode !== undefined) setCompactMode(importData.compactMode);
                        if (importData.colorScheme) setColorScheme(importData.colorScheme);
                        if (importData.fontSize) setFontSize(importData.fontSize);
                        if (importData.highContrast !== undefined) setHighContrast(importData.highContrast);
                        if (importData.reducedMotion !== undefined) setReducedMotion(importData.reducedMotion);
                        if (importData.preferences) setPreferences(importData.preferences);

                        resolve('Preferences imported successfully');
                    } else {
                        reject(new Error('Invalid preferences file format'));
                    }
                } catch (error) {
                    reject(new Error('Failed to parse preferences file'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    }, []);

    const value = {
        // Theme state
        theme,
        isDarkMode,
        getEffectiveTheme,
        colorScheme,
        fontSize,

        // Layout state
        sidebarCollapsed,
        sidebarPinned,
        compactMode,

        // Accessibility state
        highContrast,
        reducedMotion,

        // Preferences
        preferences,

        // Theme actions
        setTheme: setThemeMode,
        toggleTheme,
        setColorScheme: setColorSchemeMode,
        setFontSize: setFontSizeMode,

        // Layout actions
        setSidebarCollapsed,
        setSidebarPinned,
        toggleSidebar,
        toggleSidebarPin,
        setCompactMode,
        toggleCompactMode,

        // Accessibility actions
        setHighContrast,
        setReducedMotion,
        toggleHighContrast,
        toggleReducedMotion,

        // Preferences actions
        updatePreferences,
        updateNotificationPreferences,
        updateDashboardPreferences,
        updateTablePreferences,
        updateMapPreferences,
        resetPreferences,

        // Import/Export
        exportPreferences,
        importPreferences
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;